import { useState } from 'react';
import { useRouter } from 'next/router';

import { Button, ButtonGroup, Divider, Heading, Modal, TextButton } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyDetailsWebApiHandler_details } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import {
  FieldEditModalHeaderActionProvider,
  useFieldEditModalHeaderAction,
} from '../../../contexts/FieldEditModalHeaderActionContext';
import { FormModeProvider, useFormMode } from '../../../contexts/FormModeContext';

import ResetButton from '../../ResetButton';

import type { ReactNode } from 'react';

type FieldEditModalProps = {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  isSaveDisabled?: boolean;
  saveLabel?: string;
};

const FieldEditModalContent = ({
  title,
  onClose,
  children,
  isSaveDisabled,
  saveLabel = 'Save',
}: Omit<FieldEditModalProps, 'isOpen'>) => {
  const [isSaving, setIsSaving] = useState(false);
  const formModeContext = useFormMode();
  const router = useRouter();
  const queryClient = useQueryClient();

  const listingAlias = router.query.listingAlias as string;

  const { headerAction } = useFieldEditModalHeaderAction();

  const handleSave = async () => {
    if (isSaving) return;

    try {
      setIsSaving(true);

      if (formModeContext.mode === 'modal') {
        const { hasValidationErrors, hasMutationErrors, savesExecuted } = await formModeContext.triggerAllSaves();

        // Don't close - let user fix errors
        if (hasValidationErrors || hasMutationErrors) {
          return;
        }

        // Only wait for refetch if saves were actually executed
        if (savesExecuted > 0 && listingAlias) {
          // Get the query key for listingDetails
          const listingDetailsQueryKey = useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({
            listingAlias,
            includeTopDupesDetails: true,
            includeVrModels: false,
            includeHasVrModels: false,
            includeWebAnalytics: true,
            includeDeliveryMethod: true,
          });

          // Wait for automatic refetch (triggered by meta.invalidates) to complete
          // Poll with a small delay until isFetching becomes false
          while (queryClient.isFetching({ queryKey: listingDetailsQueryKey }) > 0) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
        }
      }

      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal.Portal>
      <Modal.Backdrop>
        <Modal.Content>
          <Modal.Header css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '400' }}>
            <Heading level={2}>{title}</Heading>
            {headerAction && <ResetButton ariaLabel={headerAction.ariaLabel} onReset={headerAction.onAction} />}
          </Modal.Header>
          <Divider />
          <Modal.Body>{children}</Modal.Body>
          <Divider />
          <Modal.Footer>
            <ButtonGroup aria-label={`${title} actions`}>
              <TextButton onClick={onClose} disabled={isSaving}>
                Cancel
              </TextButton>
              <Button tone="brand" emphasis="filled" onClick={handleSave} loading={isSaving} disabled={isSaveDisabled}>
                {saveLabel}
              </Button>
            </ButtonGroup>
          </Modal.Footer>
          <Modal.CloseButton />
        </Modal.Content>
      </Modal.Backdrop>
    </Modal.Portal>
  );
};

export const FieldEditModal = ({
  title,
  isOpen,
  onClose,
  children,
  isSaveDisabled,
  saveLabel,
}: FieldEditModalProps) => {
  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'md' }}
      useDismissProps={{
        outsidePress: false,
      }}
    >
      <FormModeProvider mode="modal">
        <FieldEditModalHeaderActionProvider>
          <FieldEditModalContent title={title} onClose={onClose} isSaveDisabled={isSaveDisabled} saveLabel={saveLabel}>
            {children}
          </FieldEditModalContent>
        </FieldEditModalHeaderActionProvider>
      </FormModeProvider>
    </Modal.Root>
  );
};
